/* The runs a server task started, newest first — each one a chat, opened by a
   click. Same frame as TaskHistory's dialog. */
import { Activity, Loader2 } from "lucide-react";

import PixelAvatar from "@/shared/components/PixelAvatar";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";
import { useTaskRuns } from "@/shared/api/tasks";
import { agentMood } from "@/shared/lib/agent-mood";

import type { Agent, AgentTask, ApiRunStatus, TaskRunRow } from "@/shared/types";

type Tone = "success" | "danger" | "warn" | "info";

const STATUS: Record<ApiRunStatus, { label: string; tone: Tone }> = {
  success: { label: "Done", tone: "success" },
  error: { label: "Failed", tone: "danger" },
  halted: { label: "Stopped", tone: "warn" },
  queued: { label: "Queued", tone: "info" },
  running: { label: "Running", tone: "info" },
  pending: { label: "Waiting for you", tone: "warn" },
};

const TONE: Record<Tone, string> = {
  success: "bg-[var(--success-bg)] text-[var(--success)] ring-[var(--success-ring)]",
  danger: "bg-[var(--danger-bg)] text-[var(--danger)] ring-[var(--danger-ring)]",
  warn: "bg-[var(--warn-bg)] text-[var(--warn-text)] ring-[var(--warn-ring)]",
  info: "bg-[var(--info-bg)] text-[var(--info-text)] ring-[var(--info-ring)]",
};

function when(iso: string) {
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function took(ms: number | null) {
  if (ms == null) return "—";
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
}

function RunRow({ run, onOpen }: { run: TaskRunRow; onOpen: () => void }) {
  const status = STATUS[run.status] ?? { label: run.status, tone: "info" as Tone };
  return (
    <button
      type="button"
      onClick={onOpen}
      title={run.error || "Open the chat it answered in"}
      className="press w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-[var(--accent-hover)]"
    >
      <span className={`shrink-0 rounded-full text-[11.5px] font-medium px-2 py-[2px] ${TONE[status.tone]}`}>
        {status.label}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-[var(--text-primary)] tracking-[-0.005em] font-mono tabular-nums">{when(run.started_at)}</div>
        <div className="text-[11.5px] text-[var(--text-tertiary)] truncate">
          {run.trigger === "schedule" ? "On its schedule" : "Run by hand"}
          {run.error ? ` · ${run.error}` : ""}
        </div>
      </div>
      <span className="shrink-0 text-[12px] text-[var(--text-tertiary)] font-mono tabular-nums">{took(run.duration_ms)}</span>
      <span className="shrink-0 w-[64px] text-right text-[12px] text-[var(--text-tertiary)] font-mono tabular-nums">
        ${run.cost_usd.toFixed(4)}
      </span>
    </button>
  );
}

export interface TaskRunsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Looked up with `find`, so absent is a real state. */
  task?: AgentTask;
  agent: Agent;
  onOpenChat: (threadId: string) => void;
}

export default function TaskRunsDialog({ open, onOpenChange, task, agent, onOpenChat }: TaskRunsDialogProps) {
  const { data: runs, isLoading } = useTaskRuns(task?.id ?? null);
  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px] p-0 gap-0 rounded-[16px] bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog overflow-hidden">
        <div className="px-5 pt-5 pb-4 border-b hairline">
          <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)] flex items-center gap-2">
            <Activity className="w-4 h-4 text-[var(--text-tertiary)]" strokeWidth={1.75} />
            Runs
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-[var(--text-tertiary)] mt-1 flex items-center gap-1.5">
            <PixelAvatar seed={agent.seed} hue={agent.hue} emotion={agentMood(agent)} size={13} rounded={4} />
            {task.name} · each run answers in its own chat
          </DialogDescription>
        </div>

        <div className="max-h-[65vh] overflow-y-auto px-5 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-[var(--text-tertiary)]">
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.75} />
            </div>
          ) : runs?.length ? (
            <div className="rounded-[12px] ring-1 ring-[var(--hairline)] overflow-hidden divide-y divide-[var(--hairline)] bg-[var(--surface)]">
              {runs.map((run) => (
                <RunRow key={run.id} run={run} onOpen={() => onOpenChat(run.thread_id)} />
              ))}
            </div>
          ) : (
            <p className="text-[12.5px] text-[var(--text-quaternary)] italic m-0 py-6 text-center">
              No runs yet — it runs at its next slot, or press Run.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
